import type { FC, MouseEventHandler } from 'react';
import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { extendAnalyticsData, getCompetitorsLineName, selectUserIsAuthenticated, useLineBehaviorV2, useWebsocketLineShort } from '@mwl/core-lib';
import { Button } from '@/components/Button/Button';
import { MatchTeam } from '@/components/MatchTeam/MatchTeam';
import { base, lineRowLinkDataAttributes, routes } from '@/constants';
import { useTypedSelector } from '@/hooks';
import type { LineRowProps, LineWithEntities } from '@/types';
import { MatchStatus } from '../MatchStatus/MatchStatus';
import { SportButtonFavorites } from '../SportButtonFavorites/SportButtonFavorites';
import { SportCompetitorLeadSideIcon } from '../SportCompetitorLeadSideIcon/SportCompetitorLeadSideIcon';
import { LineRowOdds } from './components/LineRowOdds/LineRowOdds';
import { LineRowScores } from './components/LineRowScores/LineRowScores';
import styles from './LineRow.module.scss';
export const LineRow: FC<LineRowProps> = ({
  line: initialLine,
  isFavorite,
  className,
  analytics,
  isInSearch
}) => {
  const router = useRouter();
  const [isMatchButtonHovered, setIsMatchButtonHovered] = useState(false);
  const line = useWebsocketLineShort<LineWithEntities>(initialLine, base.NAME_PROJECT);
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const {
    lineHref,
    competitors,
    singleCompetitor
  } = useLineBehaviorV2({
    line: initialLine,
    routes
  });
  const handleClick = useCallback<MouseEventHandler<HTMLElement>>(event => {
    const target = (event.target as HTMLElement);
    if (Object.keys(lineRowLinkDataAttributes).every(key => target.hasAttribute(key))) {
      router.push(lineHref);
    }
  }, [router, lineHref]);
  return <div className={cn(styles.root, className, {
    [styles.lineInSearch]: isInSearch,
    [styles.hovered]: isMatchButtonHovered
  })} onClick={handleClick} role="presentation" data-sentry-component="LineRow" data-sentry-source-file="LineRow.tsx">
      <div {...lineRowLinkDataAttributes} className={cn(styles.content, styles[line.stage.toLowerCase()])}>
        <div {...lineRowLinkDataAttributes} className={styles.controls}>
          <div {...lineRowLinkDataAttributes} className={styles.info}>
            {isAuth && <SportButtonFavorites className={styles.favoriteButton} size="xs" lineId={line.id} isFavorite={isFavorite} color="red" analytics={extendAnalyticsData(isFavorite ? analytics?.removeFavoriteLine : analytics?.addFavoriteLine, {
            line_id: line.id
          })} />}
            <MatchStatus className={styles.matchStatus} beginAt={line.beginAt} stage={line.stage} status={line.status} currentSegment={line.stats?.currentSegment} clock={line.stats?.clock} data-sentry-element="MatchStatus" data-sentry-source-file="LineRow.tsx" />
          </div>

          <Button as="link" className={styles.matchButton} href={lineHref} variant="text" onMouseEnter={() => setIsMatchButtonHovered(true)} onMouseLeave={() => setIsMatchButtonHovered(false)} analytics={extendAnalyticsData(isInSearch ? analytics?.matchNameInSearch : analytics?.matchName, {
          href: lineHref,
          text: getCompetitorsLineName(line.competitors),
          category: line.category.title,
          championships: line.championship.title,
          match_id: line.championshipId,
          line_id: line.id
        })} data-sentry-element="Button" data-sentry-source-file="LineRow.tsx">
            <div className={styles.matchTeams}>
              <div className={styles.teamRow}>
                <MatchTeam {...singleCompetitor ?? competitors.HOME} direction="row" sportCode={initialLine.sport.code} sportIcon={initialLine.sport.icon} data-sentry-element="MatchTeam" data-sentry-source-file="LineRow.tsx" />
                <SportCompetitorLeadSideIcon side="HOME" line={line} data-sentry-element="SportCompetitorLeadSideIcon" data-sentry-source-file="LineRow.tsx" />
              </div>

              {!singleCompetitor && <div className={styles.teamRow}>
                  <MatchTeam {...competitors.AWAY} direction="row" sportCode={initialLine.sport.code} sportIcon={initialLine.sport.icon} />
                  <SportCompetitorLeadSideIcon side="AWAY" line={line} />
                </div>}
            </div>
          </Button>
        </div>

        <LineRowScores line={line} analytics={analytics} data-sentry-element="LineRowScores" data-sentry-source-file="LineRow.tsx" />
      </div>

      <LineRowOdds lineId={line.id} lineStage={line.stage} markets={line.topMarkets} linePaused={line.status !== 'OPENED'} lineHref={lineHref} analytics={isInSearch ? analytics?.sportLinesOddSelectInSearch : analytics?.sportLinesOddSelect} data-sentry-element="LineRowOdds" data-sentry-source-file="LineRow.tsx" />
    </div>;
};